import { type FormEvent, useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { api, apiUrl, messageOf, type DocumentResponse, type PolicyResponse } from "../lib/api.ts";
import { useToken } from "../lib/auth.tsx";
import type { AgentOutlet } from "./AgentLayout.tsx";

export function Install() {
  const { agent, reload, progress } = useOutletContext<AgentOutlet>();
  const token = useToken();
  const [domains, setDomains] = useState(agent.allowedDomains.join("\n"));
  const [draftId, setDraftId] = useState<string | null>(null);
  const [policiesReady, setPoliciesReady] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [pending, setPending] = useState(false);
  const snippet = `<script src="${apiUrl()}/widget.js" data-agent="${agent.publicKey}"></script>`;
  const domainList = lines(domains);
  const domainsMatch = same(domainList, agent.allowedDomains);
  const live = Boolean(agent.publishedPolicyVersionId) && !draftId && domainsMatch && agent.allowedDomains.length > 0;

  useEffect(() => {
    let active = true;
    setPoliciesReady(false);
    void api<{ policies: PolicyResponse[] }>(`/v1/agents/${agent.id}/policies`, token)
      .then((body) => {
        if (!active) return;
        const latest = body.policies[0];
        setDraftId(latest?.status === "draft" ? latest.id : null);
        setPoliciesReady(true);
      })
      .catch((caught: unknown) => {
        if (active) setError(messageOf(caught));
      });
    return () => {
      active = false;
    };
  }, [agent.id, agent.publishedPolicyVersionId, token]);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!progress.knowledge) return;
    setPending(true);
    setError("");
    setNotice("");
    try {
      if (live) {
        try {
          await navigator.clipboard.writeText(snippet);
          setNotice("Install code copied. Paste it before the closing body tag on your site.");
        } catch {
          setError("Select the install code and copy it manually.");
        }
        return;
      }
      if (domainList.length === 0) {
        setError("Add the website where the chat should appear.");
        return;
      }
      if (!draftId && !agent.publishedPolicyVersionId) {
        setError("Save how it answers before making it live.");
        return;
      }
      await api(`/v1/agents/${agent.id}`, token, { method: "PATCH", json: { allowedDomains: domainList } });
      if (draftId) {
        await api(`/v1/agents/${agent.id}/policies/${draftId}/publish`, token, { method: "POST" });
        setDraftId(null);
      }
      await reload();
      setNotice(draftId ? "It’s live. Copy the install code and paste it on your site." : "Saved the websites where the chat can appear.");
    } catch (caught) {
      setError(messageOf(caught));
    } finally {
      setPending(false);
    }
  }

  const label = !progress.knowledge ? "Add a file first" : live ? "Copy the install code" : draftId || !agent.publishedPolicyVersionId ? "Make it live" : "Save websites";

  return (
    <div className="stack">
      <header className="step-head">
        <p className="eyebrow">Step 6 of 6</p>
        <h1>Install on your site</h1>
        <p className="lead">Name the websites where the chat may appear, make it live, then paste one line of code before the closing body tag.</p>
      </header>
      {!progress.knowledge ? (
        <p className="banner">
          Add a file before you put this on your site. <Link to="../knowledge">Add your knowledge</Link>
        </p>
      ) : null}
      <form className="card stack" onSubmit={onSubmit}>
        <label>
          Websites where the chat can appear
          <textarea rows={4} value={domains} onChange={(event) => setDomains(event.target.value)} placeholder={"yourbusiness.com\n*.yourbusiness.com"} />
        </label>
        <p className="muted">One website per line. A leading *. covers subdomains. The chat stays hidden everywhere else.</p>
        <section className={live ? "snippet" : "snippet waiting"}>
          <pre>{snippet}</pre>
        </section>
        {!live ? <p className="muted">The install code works after you make the chat live.</p> : null}
        {error ? <p className="banner bad">{error}</p> : null}
        {notice ? <p className="banner good">{notice}</p> : null}
        {progress.knowledge ? (
          <button className="button block" type="submit" disabled={pending || !policiesReady}>
            {pending ? "Saving…" : label}
          </button>
        ) : (
          <Link className="button block" to="../knowledge">
            Add a file first
          </Link>
        )}
      </form>
      <ReadyCheck agentId={agent.id} />
    </div>
  );
}

function ReadyCheck({ agentId }: { agentId: string }) {
  const token = useToken();
  const [waiting, setWaiting] = useState(false);

  useEffect(() => {
    let active = true;
    void api<{ documents: DocumentResponse[] }>(`/v1/agents/${agentId}/documents`, token).then((body) => {
      if (active) setWaiting(body.documents.some((document) => document.status === "queued" || document.status === "processing"));
    });
    return () => {
      active = false;
    };
  }, [agentId, token]);

  if (!waiting) return null;
  return <p className="banner">A file is still being read. The live chat will use it when it is ready.</p>;
}

function lines(value: string): string[] {
  return value
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}

function same(left: string[], right: string[]): boolean {
  if (left.length !== right.length) return false;
  return left.every((item, index) => item.toLowerCase() === (right[index] ?? "").toLowerCase());
}
